import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAudienceState } from './hooks';
import { useAudienceIdentity, SpectatorProvider } from './context/AudienceIdentityContext';
import ParchmentLayout from './components/ParchmentLayout';
import SheetVoteResultsScreen from './pages/SheetVoteResultsScreen';
import SheetVoteWinnerScreen from './pages/SheetVoteWinnerScreen';
import StandbyScreen from './pages/StandbyScreen';
import CharacterSheetView from './pages/CharacterSheetView';
import JoinScreen from './pages/JoinScreen';
import WelcomeScreen from './pages/WelcomeScreen';
import PollPrepareScreen from './pages/PollPrepareScreen';
import PollVotingScreen from './pages/PollVotingScreen';
import PollResultsScreen from './pages/PollResultsScreen';
import PollWaitingScreen from './pages/PollWaitingScreen';
import BondVotingScreen from './pages/BondVotingScreen';
import BondResultsScreen from './pages/BondResultsScreen';
import BondWinnerScreen from './pages/BondWinnerScreen';
import BondWaitingScreen from './pages/BondWaitingScreen';
import LotteryVotingScreen from './pages/LotteryVotingScreen';
import LotteryWinnerScreen from './pages/LotteryWinnerScreen';
import MoleChallengeScreen from './pages/MoleChallengeScreen';
import BalanceChallengeScreen from './pages/BalanceChallengeScreen';
import PixelArtChallengeScreen from './pages/PixelArtChallengeScreen';
import ExploreChallengeScreen from './pages/ExploreChallengeScreen';
import ChallengeResultScreen from './pages/ChallengeResultScreen';
import ConsiglioScreen from './pages/ConsiglioScreen';
import BattleScreen from './pages/BattleScreen';

/**
 * Audience App Root
 *
 * Supporta modalità spectator via query params:
 * ?spectator=true&session=X&team=Y&audienceId=Z
 */
function AudienceApp() {
  const params = new URLSearchParams(window.location.search);
  const isSpectator = params.get('spectator') === 'true';
  const spectatorSession = params.get('session');
  const spectatorTeam = params.get('team');
  const spectatorAudienceId = params.get('audienceId');

  if (isSpectator && spectatorSession && spectatorTeam && spectatorAudienceId) {
    return (
      <SpectatorProvider
        sessionId={spectatorSession}
        teamColor={spectatorTeam}
        audienceId={spectatorAudienceId}
      >
        <Routes>
          <Route path="/" element={<AudienceMainApp />} />
          <Route path="*" element={<AudienceMainApp />} />
        </Routes>
      </SpectatorProvider>
    );
  }

  return (
    <Routes>
      {/* Join flow - per nuovi utenti */}
      <Route path="/join" element={<JoinScreen />} />

      {/* Welcome screen - dopo il join, prima dello standby */}
      <Route path="/welcome" element={<WelcomeScreen />} />

      {/* Main audience experience - per utenti già connessi */}
      <Route path="/" element={<AudienceMainApp />} />
    </Routes>
  );
}


/**
 * Audience Main App
 *
 * L'esperienza audience è completamente pilotata dall'admin.
 * Le schermate cambiano automaticamente in base agli eventi server.
 */
function AudienceMainApp() {
  const navigate = useNavigate();
  const identity = useAudienceIdentity();

  // Check if user has joined - if not, redirect to join page (skip in spectator mode)
  const storedAudienceId = identity.audienceId;
  const storedSessionId = identity.sessionId;
  const storedTeamColor = identity.teamColor;

  useEffect(() => {
    if (!identity.isSpectator && (!storedAudienceId || !storedSessionId || !storedTeamColor)) {
      // User hasn't joined yet, redirect to join
      navigate('/audience/join', { replace: true });
    }
  }, [storedAudienceId, storedSessionId, storedTeamColor, navigate, identity.isSpectator]);

  // Get teamColor from identity context
  const teamColor = (storedTeamColor || 'BLUE').toUpperCase();

  const { state, previousState, isConnected, wasEverConnected, stateVersion, viewCharacterSheet, returnToPreviousState, activeVotingTeam, canVoteInSequential } =
    useAudienceState(teamColor, identity.isSpectator ? {
      sessionId: identity.sessionId || undefined,
      audienceId: identity.audienceId || undefined,
      nickname: identity.nickname || undefined,
      isSpectator: true,
    } : undefined);

  // Determine active state - se siamo in viewing-sheet, usiamo previousState per sapere se la votazione era attiva
  const activeState = state.type === 'viewing-sheet' ? previousState : state;

  // Stato centralizzato per il voto - condiviso tra StandbyScreen e CharacterSheetView
  const [hasVoted, setHasVoted] = useState(false);
  const [votedForSheetId, setVotedForSheetId] = useState<string | null>(null);

  // Reset vote state when voting session ends (non quando si va in viewing-sheet)
  const currentVoteId = activeState.type === 'sheet-vote' ? activeState.voteId : null;
  useEffect(() => {
    if (currentVoteId === null) {
      setHasVoted(false);
      setVotedForSheetId(null);
    }
  }, [currentVoteId]);

  // Restore hasAlreadyVoted + votedForSheetId from server on reconnect
  useEffect(() => {
    if (activeState.type === 'sheet-vote' && activeState.hasAlreadyVoted) {
      setHasVoted(true);
      if (activeState.votedForSheetId) {
        setVotedForSheetId(activeState.votedForSheetId);
      }
    }
  }, [stateVersion]);

  // Initial connection: show fullscreen spinner
  if (!isConnected && !wasEverConnected) {
    return (
      <ParchmentLayout teamColor={teamColor} className="flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400 mx-auto mb-4"></div>
          <p className="text-ink-muted text-lg font-heading">Connessione al server...</p>
        </div>
      </ParchmentLayout>
    );
  }

  // Determine voting state
  const votingState = activeState.type === 'sheet-vote'
    ? { isActive: true, voteId: activeState.voteId, canVote: activeState.canVote && canVoteInSequential, hasVoted, votedForSheetId }
    : { isActive: false, voteId: null, canVote: false, hasVoted: false, votedForSheetId: null };

  // Callback per quando l'utente vota
  const handleVoteComplete = (sheetId: string) => {
    setHasVoted(true);
    setVotedForSheetId(sheetId);
  };

  // In SEQUENTIAL mode, check if results/winner screens are for my team
  const isMyTeamTurn = activeVotingTeam === null || activeVotingTeam === teamColor;

  return (
    <ParchmentLayout teamColor={teamColor}>
      {/* Banner di riconnessione non-invasivo (mantiene visibile lo schermo corrente) */}
      {!isConnected && wasEverConnected && (
        <div className="fixed top-0 left-0 right-0 bg-team-red/90 text-white text-center py-2 z-50 font-heading text-sm">
          Riconnessione in corso...
        </div>
      )}

      {/* Render basato sullo stato corrente - pilotato dal server */}
      {(state.type === 'standby' || state.type === 'sheet-vote') && (
        <StandbyScreen
          onViewCharacter={viewCharacterSheet}
          votingState={votingState}
          waitingForTeam={state.type === 'sheet-vote' && !canVoteInSequential ? activeVotingTeam : null}
          onVoteComplete={handleVoteComplete}
        />
      )}

      {state.type === 'sheet-vote-results' && (
        isMyTeamTurn ? (
          <SheetVoteResultsScreen voteId={state.voteId} />
        ) : (
          <StandbyScreen
            onViewCharacter={viewCharacterSheet}
            votingState={{ isActive: false, voteId: null, canVote: false, hasVoted: false, votedForSheetId: null }}
            waitingForTeam={activeVotingTeam}
            onVoteComplete={handleVoteComplete}
          />
        )
      )}

      {state.type === 'sheet-vote-winner' && (
        isMyTeamTurn ? (
          <SheetVoteWinnerScreen sheetId={state.sheetId} sheetName={state.sheetName} showCelebration={state.isCelebration} />
        ) : (
          <StandbyScreen
            onViewCharacter={viewCharacterSheet}
            votingState={{ isActive: false, voteId: null, canVote: false, hasVoted: false, votedForSheetId: null }}
            waitingForTeam={activeVotingTeam}
            onVoteComplete={handleVoteComplete}
          />
        )
      )}

      {state.type === 'vote' && (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-ink mb-4">Votazione</h2>
            <p className="text-ink-muted">
              {state.canVote ? 'Esprimi il tuo voto!' : 'Votazione in corso...'}
            </p>
          </div>
        </div>
      )}

      {state.type === 'challenge' && (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-ink mb-4">Sfida</h2>
            <p className="text-ink-muted">
              {state.isMyTeam ? 'Preparati!' : 'Sfida in corso...'}
            </p>
          </div>
        </div>
      )}

      {state.type === 'challenge-active' && state.variant === 'explore' && (
        <ExploreChallengeScreen
          key={`explore-${stateVersion}`}
          challengeId={state.challengeId}
          config={state.config}
          initialScore={state.score}
          targetScore={state.targetScore}
        />
      )}

      {state.type === 'challenge-active' && state.variant === 'mole' && (
        <MoleChallengeScreen
          key={`mole-${stateVersion}`}
          challengeId={state.challengeId}
          config={state.config}
          endsAt={state.endsAt}
          serverTime={state.serverTime}
          initialScore={state.score}
          targetScore={state.targetScore}
        />
      )}

      {state.type === 'challenge-active' && state.variant === 'balance' && (
        <BalanceChallengeScreen
          key={`balance-${stateVersion}`}
          challengeId={state.challengeId}
          config={state.config}
          endsAt={state.endsAt}
          serverTime={state.serverTime}
          initialScore={state.score}
          targetScore={state.targetScore}
          initialWhiteClicks={state.whiteClicks}
          initialBlackClicks={state.blackClicks}
        />
      )}

      {state.type === 'challenge-active' && state.variant === 'pixelart' && (
        <PixelArtChallengeScreen
          key={`pixelart-${stateVersion}`}
          challengeId={state.challengeId}
          config={state.config}
          endsAt={state.endsAt}
          serverTime={state.serverTime}
          numColors={state.numColors || 4}
          initialUserGrid={state.userGrid}
        />
      )}

      {state.type === 'challenge-result' && (
        <ChallengeResultScreen
          challengeId={state.challengeId}
          result={state.result}
          variant={state.variant}
          personalAccuracy={state.personalAccuracy}
          averageAccuracy={state.averageAccuracy}
        />
      )}

      {state.type === 'result-display' && (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-ink mb-4">Risultati</h2>
            <p className="text-ink-muted">Tipo: {state.resultType}</p>
            <pre className="text-xs text-ink-muted mt-4">
              {JSON.stringify(state.data, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {state.type === 'viewing-sheet' && (
        <CharacterSheetView
          characterId={state.characterId}
          onBack={returnToPreviousState}
          votingState={votingState}
          onVoteComplete={handleVoteComplete}
        />
      )}

      {state.type === 'poll-prepare' && (
        <PollPrepareScreen
          targetTeam={state.targetTeam}
          isMyTeam={state.targetTeam === null || state.targetTeam === teamColor}
        />
      )}

      {state.type === 'poll-voting' && (
        <PollVotingScreen
          key={`poll-${stateVersion}`}
          pollId={state.pollId}
          options={state.options}
          endsAt={state.endsAt}
          serverTime={state.serverTime}
          canVote={state.canVote}
          initialVotedOptionId={state.votedOptionId}
        />
      )}

      {state.type === 'poll-results' && (
        <PollResultsScreen pollId={state.pollId} />
      )}

      {state.type === 'poll-waiting' && (
        <PollWaitingScreen message={state.message} />
      )}

      {state.type === 'bond-vote' && (
        <BondVotingScreen
          voteId={state.voteId}
          options={state.options}
          canVote={state.canVote}
          initialVotedOptionId={state.votedOptionId}
        />
      )}

      {state.type === 'bond-vote-waiting' && (
        <BondWaitingScreen message={state.message} />
      )}

      {state.type === 'bond-vote-results' && (
        <BondResultsScreen voteId={state.voteId} />
      )}

      {state.type === 'bond-vote-winner' && (
        <BondWinnerScreen
          voteId={state.voteId}
          fromTeam={state.fromTeam}
          heroName={state.heroName}
          heroImageUrl={state.heroImageUrl}
          bondText={state.bondText}
        />
      )}

      {state.type === 'lottery-voting' && (
        <LotteryVotingScreen
          key={`lottery-${stateVersion}`}
          lotteryId={state.lotteryId}
          endsAt={state.endsAt}
          serverTime={state.serverTime}
          texts={state.texts}
          initialHasVoted={state.hasAlreadyVoted}
          initialVotedYes={state.votedYes}
        />
      )}

      {state.type === 'lottery-winner' && (
        <LotteryWinnerScreen
          lotteryId={state.lotteryId}
          isWinner={state.isWinner}
          texts={state.texts}
        />
      )}

      {state.type === 'consiglio' && (
        <ConsiglioScreen
          key={`consiglio-${stateVersion}`}
          endsAt={state.endsAt}
          serverTime={state.serverTime}
          initialHasSubmitted={state.hasSubmitted}
        />
      )}

      {state.type === 'battle' && (
        <BattleScreen
          key={`battle-${stateVersion}`}
          battleState={state.battleState}
        />
      )}
    </ParchmentLayout>
  );
}

export { AudienceMainApp };
export default AudienceApp;
